import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ShieldCheck, TrendingUp, Filter, ArrowRight, Users, Landmark } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/shared";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const REGIONS = ["all", "Europe", "APAC", "Global"];
const CLASSES = ["all", "Real Estate", "Private Credit", "Fixed Income", "Infrastructure", "Commodities", "Venture"];

const fmt = (n, d = 0) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function Investors({ onCta }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [region, setRegion] = useState("all");
  const [asset, setAsset] = useState("all");
  const [selected, setSelected] = useState(null);
  const [ticket, setTicket] = useState("");
  const [eligible, setEligible] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/offerings", { params: { region: region !== "all" ? region : undefined, asset_class: asset !== "all" ? asset : undefined } })
      .then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [region, asset]);

  const openSubscribe = (o) => {
    if (!user) { navigate("/login"); return; }
    setSelected(o); setTicket(String(o.min_ticket)); setEligible("");
  };

  const submitInterest = async () => {
    if (!eligible) { toast.error("Please confirm your eligibility"); return; }
    if (Number(ticket) < selected.min_ticket) { toast.error(`Minimum ticket is $${selected.min_ticket}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/subscriptions", {
        offering_id: selected.id,
        ticket: Number(ticket),
        eligibility: eligible,
      });
      toast.success(`Subscribed — $${data.ticket} allocated, ${data.schedule?.length || 0} scheduled payouts`);
      setSelected(null);
      navigate("/dashboard?v=subscriptions");
    } catch (e) {
      const msg = e.response?.data?.detail || "";
      if (typeof msg === "string" && msg.toLowerCase().includes("balance")) {
        toast.error("Insufficient wallet balance — deposit funds first");
        navigate("/dashboard?v=deposit");
      } else {
        toast.error(apiError(e.response?.data?.detail));
      }
    }
    setBusy(false);
  };

  return (
    <>
      <Navbar onCta={onCta} />
      <main>
        {/* HERO */}
        <section className="pt-40 pb-16 relative">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[600px] blue-glow pointer-events-none" />
          <div className="container-auvra relative z-10">
            <div className="max-w-3xl">
              <Reveal><span className="kicker mb-4 block">Investors</span></Reveal>
              <Reveal i={1}><h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.05]">Institutional-grade access to <span className="text-auvra-blue">tokenized real world assets</span></h1></Reveal>
              <Reveal i={2}><p className="mt-6 text-lg text-secondary-auvra leading-relaxed">Curated offerings across real estate, private credit, infrastructure, fixed income and venture — settled on BNB Smart Chain with institutional custody and compliance.</p></Reveal>
              <Reveal i={3}><div className="mt-8 flex flex-wrap gap-3">
                <Link to="/signup" className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">Start Investing <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/whitepaper" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white font-medium px-6 py-3 rounded-full transition-all hover:bg-white/5">Read the Whitepaper</Link>
              </div></Reveal>
            </div>
          </div>
        </section>

        {/* Investor types */}
        <section className="py-12">
          <div className="container-auvra grid md:grid-cols-3 gap-4">
            {[
              { I: Landmark, t: "Institutional", d: "Direct-relationship coverage, structured tickets, on-chain custody, KYB and reporting integration." },
              { I: Users, t: "Family Office", d: "Private allocations, diversified RWA baskets, quarterly reporting and dedicated relationship management." },
              { I: ShieldCheck, t: "Accredited & Retail (KYC)", d: "Access curated offerings from $100 with on-chain transparency and clear eligibility gating." },
            ].map((c, i) => (
              <Reveal key={c.t} i={i}>
                <div className="glass rounded-2xl p-8 h-full">
                  <c.I className="w-6 h-6 text-auvra-blue mb-4" />
                  <h3 className="text-xl font-semibold tracking-tight">{c.t}</h3>
                  <p className="text-sm text-secondary-auvra mt-2 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Offerings marketplace */}
        <section className="py-12">
          <div className="container-auvra">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <Reveal><span className="kicker mb-3 block">Marketplace</span></Reveal>
                <Reveal i={1}><h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Curated tokenized offerings</h2></Reveal>
              </div>
              <div className="flex flex-wrap gap-2">
                <Filter className="w-4 h-4 text-secondary-auvra mt-3" />
                <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="filter-region" className="bg-black border border-white/15 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-auvra-blue">
                  {REGIONS.map((r) => <option key={r} value={r}>{r === "all" ? "All regions" : r}</option>)}
                </select>
                <select value={asset} onChange={(e) => setAsset(e.target.value)} data-testid="filter-class" className="bg-black border border-white/15 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-auvra-blue">
                  {CLASSES.map((c) => <option key={c} value={c}>{c === "all" ? "All classes" : c}</option>)}
                </select>
              </div>
            </div>

            {items.length === 0 ? (
              <p className="text-secondary-auvra text-sm">No offerings match your filters.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4" data-testid="offerings-grid">
                {items.map((o) => (
                  <Reveal key={o.id}>
                    <div className="glass rounded-2xl p-6 h-full flex flex-col" data-testid={`offer-${o.id}`}>
                      <div className="flex gap-4">
                        <img src={o.image} alt="" className="w-28 h-28 object-cover rounded-xl flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex gap-2 flex-wrap mb-2">
                            <Chip tone={o.status === "OPEN" ? "green" : "yellow"}>{o.status}</Chip>
                            <Chip tone="gray">{o.class}</Chip>
                            <Chip tone="blue">{o.region}</Chip>
                          </div>
                          <h3 className="text-xl font-semibold tracking-tight">{o.title}</h3>
                          <p className="text-sm text-secondary-auvra mt-1 line-clamp-2">{o.summary}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-3 mt-5 text-xs">
                        <div><div className="text-secondary-auvra">Yield</div><div className="text-auvra-blue text-sm font-semibold">{o.target_yield}%</div></div>
                        <div><div className="text-secondary-auvra">Term</div><div className="text-sm">{o.term_months}m</div></div>
                        <div><div className="text-secondary-auvra">Min</div><div className="text-sm">${fmt(o.min_ticket)}</div></div>
                        <div><div className="text-secondary-auvra">Region</div><div className="text-sm">{o.region}</div></div>
                      </div>
                      <div className="mt-4">
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-auvra-blue" style={{ width: `${Math.min(100, (o.raised / o.target) * 100)}%` }} />
                        </div>
                        <div className="text-[10px] text-secondary-auvra mt-1 flex justify-between"><span>${fmt(o.raised / 1e6, 2)}M raised</span><span>${fmt(o.target / 1e6, 1)}M target</span></div>
                      </div>
                      <div className="mt-5 flex flex-wrap gap-2 items-center justify-between">
                        <span className="text-xs text-secondary-auvra">Eligibility: <span className="text-white">{o.eligibility}</span></span>
                        <button onClick={() => openSubscribe(o)} data-testid={`subscribe-${o.id}`} className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white text-sm font-medium px-4 py-2 rounded-full transition-all">Subscribe <ArrowRight className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Process */}
        <section className="py-16">
          <div className="container-auvra">
            <Reveal><span className="kicker mb-3 block">Process</span></Reveal>
            <Reveal i={1}><h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-10">Onboarding to allocation in four steps</h2></Reveal>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["01", "Create account", "Register with email or Google. Get a virtual wallet and dashboard access instantly."],
                ["02", "Complete KYC", "Verify your identity to unlock premium offerings and withdrawals."],
                ["03", "Fund your wallet", "Deposit via BEP20 (BNB Smart Chain) or TRC20 (Tron). Instant settlement."],
                ["04", "Subscribe", "Allocate to curated offerings, receive on-chain tokens and periodic distributions."],
              ].map(([n, t, d], i) => (
                <Reveal key={n} i={i}>
                  <div className="glass rounded-2xl p-6 h-full">
                    <div className="text-xs tracking-[0.2em] text-auvra-blue mb-3">{n}</div>
                    <h4 className="font-medium">{t}</h4>
                    <p className="text-sm text-secondary-auvra mt-2 leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-auvra-elevated border border-white/10 text-white sm:max-w-lg" data-testid="subscribe-modal">
          {selected && (
            <>
              <DialogHeader><DialogTitle>Subscribe · {selected.title}</DialogTitle></DialogHeader>
              <div className="space-y-5 mt-2">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><div className="text-xs text-secondary-auvra">Yield</div><div className="text-auvra-blue font-semibold">{selected.target_yield}%</div></div>
                  <div><div className="text-xs text-secondary-auvra">Term</div><div>{selected.term_months}m</div></div>
                  <div><div className="text-xs text-secondary-auvra">Min</div><div>${fmt(selected.min_ticket)}</div></div>
                </div>
                <label className="block">
                  <span className="block text-xs uppercase tracking-wider text-secondary-auvra mb-2">Ticket Size (USD)</span>
                  <input type="number" value={ticket} onChange={(e) => setTicket(e.target.value)} data-testid="sub-ticket"
                    className="w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-auvra-blue" />
                </label>
                <div>
                  <span className="block text-xs uppercase tracking-wider text-secondary-auvra mb-2">Eligibility (self-attestation)</span>
                  <div className="space-y-2">
                    {["Retail (KYC)", "Accredited Investor", "Qualified Investor", "Institutional"].map((opt) => (
                      <label key={opt} className="flex items-center gap-3 text-sm cursor-pointer">
                        <input type="radio" name="elig" checked={eligible === opt} onChange={() => setEligible(opt)} data-testid={`elig-${opt.split(" ")[0]}`} className="accent-auvra-blue" />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-secondary-auvra bg-white/[0.02] border border-white/5 rounded-xl p-3">
                  Required eligibility for this offering: <span className="text-white">{selected.eligibility}</span>. Subscription documents and appropriateness checks will be sent to your registered email.
                </div>
                <button onClick={submitInterest} disabled={busy} data-testid="submit-interest"
                  className="w-full bg-auvra-blue hover:bg-auvra-blue/90 disabled:opacity-60 text-white font-medium py-3.5 rounded-xl transition-all">
                  {busy ? "Submitting…" : "Submit Interest"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </>
  );
}

function Chip({ children, tone = "gray" }) {
  const tones = {
    blue: "bg-auvra-blue/10 border-auvra-blue/30 text-auvra-blue",
    green: "bg-green-500/10 border-green-500/30 text-green-400",
    yellow: "bg-yellow-500/10 border-yellow-500/30 text-yellow-400",
    gray: "bg-white/5 border-white/15 text-secondary-auvra",
  };
  return <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${tones[tone]}`}>{children}</span>;
}
